import { PaletteMode } from '@mui/material';
import { grey, red } from '@mui/material/colors';
import { unstable_createMuiStrictModeTheme as createTheme } from '@mui/material/styles';
import shadows from '@mui/material/styles/shadows';

// Nút lấy từ brand. Nền gần trắng, thanh bên xanh đêm, nhấn cyan để khối không bị chìm.
import { getBrand } from 'config/brand';

const FALLBACK_PRIMARY = '#0A78A8';

const mixHex = (from: string, to: string, amount: number) => {
  const parse = (hex: string) => [0, 2, 4].map((index) => parseInt(hex.slice(1 + index, 3 + index), 16));
  const [r1, g1, b1] = parse(from);
  const [r2, g2, b2] = parse(to);
  const mix = (a: number, b: number) => Math.round(a + (b - a) * amount);
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${hex(mix(r1, r2))}${hex(mix(g1, g2))}${hex(mix(b1, b2))}`;
};

const brandPrimary = () => {
  const value = getBrand().primary_color;
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : FALLBACK_PRIMARY;
};

/** Màu khung ứng dụng, tách khỏi màu thương hiệu để mỗi deployment vẫn đổi được nút. */
export const SHELL = {
  sidebar: '#164863',
  sidebarDark: '#0E3044',
  sidebarText: '#F4F8FB',
  sidebarMuted: '#A9C0D0',
  accent: '#7EB6D0',
  ink: '#0E1726',
  line: '#D7E2EE',
};

const LightTheme = (mode) => {
  const primaryMain = brandPrimary();
  const background =
    mode === 'dark'
      ? {
          paper: '#0F1C2E',
          default: '#07111C',
        }
      : {
          paper: '#FFFFFF',
          default: '#F4F8FB',
        };
  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        laptop: 1024,
        tablet: 640,
        mobile: 0,
        desktop: 1280,
      },
    },
    components: {
      MuiLink: {
        styleOverrides: { root: {} },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: { height: 16, minWidth: 16, fontSize: 10 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paperFullWidth: {
            '@media (max-width: 600px)': {
              margin: 16,
              maxHeight: 'calc(100% - 32px)',
              maxWidth: 'calc(100% - 32px)',
              width: 'calc(100% - 32px)',
            },
          },
          paper: {
            borderRadius: 16,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              minHeight: 40,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              padding: '0px 8px',
              minHeight: 40,
            },
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { padding: 4 },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          positionStart: {
            marginLeft: 8,
            marginRight: 0,
          },
          positionEnd: {
            marginLeft: 0,
            marginRight: 12,
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
      },
      MuiSvgIcon: {
        defaultProps: {
          fontSize: 'small',
        },
      },
      MuiTypography: {
        defaultProps: {
          color: 'textPrimary',
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { height: 1 },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            zIndex: 0,
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '& @media (max-width: 640px) .MuiTab-root': {
              padding: '0px 8px',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          color: 'primary',
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 700,
            '@media (max-width: 600px)': {
              display: 'flex',
              gap: 2,
              minWidth: 'unset',
            },
          },
          contained: {},
          text: {},
          sizeLarge: {
            minHeight: 48,
            minWidth: 48,
          },
          sizeMedium: {
            minHeight: 40,
            minWidth: 40,
          },
          sizeSmall: {
            height: 32,
            minWidth: 32,
          },
          outlined: {
            background: background.paper,
          },
          startIcon: {
            '@media (max-width: 640px)': {
              margin: 0,
            },
          },
          colorInherit: {
            background: background.paper,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          sizeLarge: {
            minHeight: 40,
          },
          sizeMedium: {
            minHeight: 32,
          },
          sizeSmall: {
            height: 28,
            width: 28,
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          MenuProps: { disablePortal: true },
        },
        styleOverrides: {
          select: { height: 24 },
          icon: {
            right: 8,
          },
          outlined: {
            '&.MuiInputBase-readOnly ': {
              background: mode === 'dark' ? grey[800] : grey[200],
              cursor: 'not-allowed',
            },
          },
        },
      },
      MuiToolbar: {
        defaultProps: {
          style: {
            backgroundColor: 'inherit',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            '&.paper-list': {
              borderRadius: 16,
              border: `1px solid ${mode === 'dark' ? 'rgba(244, 240, 232, 0.12)' : SHELL.line}`,
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
          },
          sizeSmall: {
            fontSize: 12,
          },
        },
      },
      MuiFormLabel: {
        defaultProps: { sx: { color: 'text.primary' } },
        styleOverrides: {
          root: {
            fontSize: 12,
            paddingBottom: 8,
            lineHeight: 1,
            margin: 0,
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: {
            paddingBottom: 8,
            '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
              transform: 'unset',
              position: 'relative',
            },
            '&.MuiInputLabel-shrink': {
              transform: 'unset',
              position: 'relative',
            },
          },
          asterisk: {
            color: red[600],
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            minHeight: 8,
            height: 'auto',
            lineHeight: 1.45,
            '&:empty': { height: 8, overflow: 'hidden' },
          },
          contained: { marginLeft: 0 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            padding: 0,
            borderRadius: 10,
            '&.Mui-disabled': {
              backgroundColor: mode === 'dark' ? 'rgba(244, 246, 252, 0.04)' : grey[50],
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? 'rgba(244, 246, 252, 0.1)' : SHELL.line,
            },
          },
          notchedOutline: {
            borderColor: mode === 'dark' ? 'rgba(244, 246, 252, 0.16)' : SHELL.line,
          },
          multiline: { padding: 0 },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
              padding: '4px 8px',
            },
            '& .MuiInputBase-readOnly': {
              background: mode === 'dark' ? grey[800] : grey[200],
              cursor: 'not-allowed',
            },
          },
          inputRoot: {
            padding: 0,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-readOnly': {
              background: mode === 'dark' ? grey[800] : grey[200],
              cursor: 'not-allowed',
            },
          },
        },
      },
      MuiInputBase: {
        defaultProps: {
          sx: { backgroundColor: 'background.paper' },
        },
        styleOverrides: {
          input: {
            minHeight: 24,
          },
          root: {
            borderRadius: 10,
            fontSize: 14,
            padding: 0,
            '& .MuiInputBase-input': { padding: '8px 12px' },
            '& .MuiAutocomplete-input': {
              padding: '8px 12px',
              height: 'auto',
              minHeight: 22,
            },
            '& fieldset': {
              top: 0,
              '& legend': {
                display: 'none',
              },
            },
            '& .MuiAutocomplete-popper.MuiAutocomplete-paper': {
              padding: 0,
            },
          },
        },
      },
    },
    typography: {
      htmlFontSize: 14,
      fontSize: 14,
      button: { textTransform: 'none' },
      fontFamily: 'Be Vietnam Pro',
      h1: { fontSize: 64, lineHeight: '72px', fontWeight: 900 },
      h2: { fontSize: 52, lineHeight: '64px', fontWeight: 900 },
      h3: { fontSize: 48, lineHeight: '56px', fontWeight: 700 },
      h4: { fontSize: 32, lineHeight: '44px', fontWeight: 700 },
      h5: {
        fontSize: 28,
        lineHeight: '36px',
        fontWeight: 700,
        '@media (max-width:640px)': {
          fontSize: 20,
          lineHeight: '30px',
        },
      },
      h6: {
        fontSize: 20,
        lineHeight: '30px',
        fontWeight: 700,
        '@media (max-width:640px)': {
          fontSize: '16px',
          lineHeight: '24px',
        },
      },
      subtitle1: {
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 600,
        '@media (max-width:640px)': {
          fontSize: '14px',
          lineHeight: '22px',
        },
      },
      subtitle2: {
        fontSize: '14px',
        lineHeight: '22px',
        fontWeight: 600,
        '@media (max-width:640px)': {
          fontSize: '12px',
          lineHeight: '18px',
        },
      },
      body1: {
        fontSize: '16px',
        lineHeight: '24px',
        '@media (max-width:640px)': {
          fontSize: '14px',
          lineHeight: '22px',
        },
      },
      body2: {
        fontSize: '14px',
        lineHeight: '22px',
        '@media (max-width:640px)': {
          fontSize: '12px',
          lineHeight: '18px',
        },
      },
      caption: {
        fontSize: '12px',
        lineHeight: '18px',
        '@media (max-width:640px)': {
          fontSize: '10px',
          lineHeight: '16px',
        },
      },
      fontWeightBold: 600,
    },
    shadows: [
      'none',
      '2px 4px 10px rgba(0, 0, 0, 0.05)',
      'rgba(15, 15, 15, 0.05) 0px 0px 0px 1px, rgba(15, 15, 15, 0.1) 0px 3px 6px, rgba(15, 15, 15, 0.2) 0px 9px 24px',
      '0px 10px 16px rgba(0, 0, 0, 0.1)',
      '0px 16px 22px rgba(0, 0, 0, 0.08)',
      '0px 28px 32px rgba(0, 0, 0, 0.08)',
      '0px 32px 64px rgba(0, 0, 0, 0.08)',
      ...shadows.slice(7),
    ] as any,
    palette: {
      mode: mode,
      divider: mode === 'dark' ? 'rgba(244, 246, 252, 0.12)' : SHELL.line,
      text:
        mode === 'dark'
          ? {
              primary: '#F4F6FC',
              secondary: '#94A3B8',
              disabled: 'rgba(244, 246, 252, 0.45)',
            }
          : {
              primary: SHELL.ink,
              secondary: '#5C6B7A',
            },
      background: background,
      primary: {
        dark: mixHex(primaryMain, '#000000', 0.24),
        main: primaryMain,
        light: mixHex(primaryMain, '#ffffff', 0.86),
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#0E7490',
        light: '#E5F6FB',
      },
      grey: {
        900: '#0E1726',
        800: '#1E293B',
        700: '#334155',
        600: '#475569',
        500: '#64748B',
        400: '#94A3B8',
        300: '#CBD5E1',
        200: '#E2E8F0',
        100: '#E8F1F8',
        50: '#F4F8FB',
      },
      success: {
        main: '#46B52B',
        light: '#D4FFCD',
      },
      info: {
        main: '#00ADED',
        400: '#3DCFF4',
        300: '#63E5F9',
        200: '#97F6FD',
        100: '#CBFEFD',
      },
      error: {
        main: '#FC4C46',
        400: '#FD8474',
        300: '#FEA68F',
        200: '#FECAB5',
        100: '#FEE7DA',
      },
      warning: {
        main: '#FFAE00',
      },
    },
  });
};

export const MUITheme = (mode?: PaletteMode) => {
  return LightTheme(mode);
};
